// Real Google sign-in for Reinforce enrollment flow.
//
// Uses the same Google OAuth endpoint as the rest of the app.
// If user is already logged in (has access_token), returns their info.
// Otherwise, triggers Google sign-in flow.

import { baseURL } from "aws-config";

const GOOGLE_CLIENT_ID = "444450736740-tiiqook4ob3sjou8f2a730umrv569ehn.apps.googleusercontent.com";

// Decode JWT payload (Google id_token) to get user info
const decodeJwt = (token) => {
  try {
    const payload = token.split(".")[1];
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
};

let googleScriptLoaded = false;
let googleScriptPromise = null;

const loadGoogleScript = () => {
  if (googleScriptLoaded) return Promise.resolve();
  if (googleScriptPromise) return googleScriptPromise;

  googleScriptPromise = new Promise((resolve, reject) => {
    const src = "https://accounts.google.com/gsi/client";
    if (document.querySelector(`script[src="${src}"]`)) {
      googleScriptLoaded = true;
      return resolve();
    }
    const script = document.createElement("script");
    script.src = src;
    script.onload = () => {
      googleScriptLoaded = true;
      resolve();
    };
    script.onerror = reject;
    document.body.appendChild(script);
  });

  return googleScriptPromise;
};

/**
 * Get current user from localStorage if logged in.
 */
export const getCurrentUser = () => {
  const accessToken = localStorage.getItem("access_token");
  if (!accessToken) return null;

  // Try to get user info from stored data
  try {
    const onboardingData = JSON.parse(localStorage.getItem("onboardingData") || "{}");
    if (onboardingData.user_name || onboardingData.user_email) {
      return {
        name: onboardingData.user_name || onboardingData.name || "User",
        email: onboardingData.user_email || onboardingData.email,
        accessToken,
      };
    }
    // If we have token but no user info, return minimal user
    return { name: "User", email: null, accessToken };
  } catch {
    return { name: "User", email: null, accessToken };
  }
};

// Singleton to track active sign-in promise
let activeSignIn = null;

/**
 * Trigger Google sign-in flow.
 * Returns a promise that resolves with user info after successful sign-in.
 */
export const googleSignIn = () => {
  // Check if already logged in
  const existingUser = getCurrentUser();
  if (existingUser?.accessToken) {
    return Promise.resolve(existingUser);
  }

  // Reuse active sign-in if one is in progress
  if (activeSignIn) return activeSignIn;

  activeSignIn = new Promise((resolve, reject) => {
    loadGoogleScript()
      .then(() => {
        /* global google */
        if (!google?.accounts?.id) {
          reject(new Error("Google Sign-In not available"));
          return;
        }

        // Create a temporary container for the Google button
        const container = document.createElement("div");
        container.style.cssText = "position:fixed;top:-9999px;left:-9999px;";
        document.body.appendChild(container);

        const cleanup = () => {
          activeSignIn = null;
          container.remove();
        };

        google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: async (response) => {
            if (!response.credential) {
              cleanup();
              reject(new Error("No credential received"));
              return;
            }

            try {
              // Decode Google credential to get user info (it's a JWT)
              const googleUser = decodeJwt(response.credential);
              const userName = googleUser?.name || "User";
              const userEmail = googleUser?.email || "";

              // Send credential to backend
              const res = await fetch(`${baseURL}google/`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ auth_token: response.credential }),
              });

              if (!res.ok) {
                throw new Error("Authentication failed");
              }

              const data = await res.json();

              // Store tokens
              if (data.access_token) {
                localStorage.setItem("access_token", data.access_token);
              }
              if (data.refresh_token) {
                localStorage.setItem("refresh_token", data.refresh_token);
              }

              // Store user info for future lookups (from Google credential, not backend)
              const onboarding = JSON.parse(localStorage.getItem("onboardingData") || "{}");
              onboarding.user_name = userName;
              onboarding.user_email = userEmail;
              localStorage.setItem("onboardingData", JSON.stringify(onboarding));

              const user = {
                name: userName,
                email: userEmail,
                accessToken: data.access_token,
              };

              cleanup();
              resolve(user);
            } catch (err) {
              cleanup();
              reject(err);
            }
          },
        });

        // Render a Google Sign-In button and click it programmatically
        google.accounts.id.renderButton(container, {
          theme: "outline",
          size: "large",
          click_listener: () => {},
        });

        // Find and click the rendered button
        setTimeout(() => {
          const btn = container.querySelector('[role="button"]') || container.querySelector("div[tabindex]");
          if (btn) {
            btn.click();
          } else {
            // Fallback: try one-tap prompt
            google.accounts.id.prompt();
          }
        }, 100);
      })
      .catch((err) => {
        activeSignIn = null;
        reject(err);
      });
  });

  return activeSignIn;
};

/**
 * Sign out - clear tokens.
 */
export const signOut = () => {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
};

// For backward compatibility with mock API
export const mockGoogleSignIn = googleSignIn;
